import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { guestPagesAPI } from '../../services/api';
import AccessForm from './AccessForm';
import PortalContent from './PortalContent';
import AIAssistant from './AIAssistant';

const GuestPortal = () => {
    const { slug } = useParams();
    const { getGuestPageBySlug, getPropertyById, isInitialized } = useData();
    const { user } = useAuth();

    const [guestPage, setGuestPage] = useState(null);
    const [property, setProperty] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadData = async () => {
            setError(null);
            setLoading(true);
            
            try {
                // First try to get from context if logged in (faster)
                if (isInitialized && user) {
                    const page = getGuestPageBySlug(slug);
                    if (page) {
                        const prop = getPropertyById(page.propertyId);
                        if (prop) {
                            setGuestPage(page);
                            setProperty(prop);
                            setLoading(false);
                            return;
                        }
                    }
                }
                
                // Otherwise fetch directly from API (works for both logged in and public access)
                const response = await guestPagesAPI.getBySlug(slug);
                
                if (!response) {
                    setError('Guest page not found');
                    setLoading(false);
                    return;
                }
                
                if (response.status === 'archived') {
                    setError('This guest portal has expired');
                    setLoading(false);
                    return;
                }
                
                // Transform API response to match frontend format
                const transformedPage = {
                    id: response.id,
                    propertyId: response.property_id,
                    guestName: response.guest_name,
                    guestEmail: response.guest_email,
                    phone: response.phone,
                    lastFourDigits: response.last_four_digits,
                    checkInDate: response.check_in_date,
                    checkOutDate: response.check_out_date,
                    doorCode: response.door_code,
                    reservationId: response.reservation_id,
                    specialInstructions: response.special_instructions,
                    status: response.status,
                    slug: response.slug,
                    createdAt: response.created_at
                };
                
                // Transform property data if included in response
                let transformedProperty = null;
                if (response.property) {
                    transformedProperty = {
                        id: response.property.id,
                        ownerId: response.property.owner_id,
                        name: response.property.name,
                        address: response.property.address,
                        image: response.property.image,
                        backgroundImage: response.property.background_image,
                        wifiName: response.property.wifi_name,
                        wifiPassword: response.property.wifi_password,
                        checkInTime: response.property.check_in_time,
                        checkOutTime: response.property.check_out_time,
                        houseRules: response.property.house_rules,
                        checkoutProcedures: response.property.checkout_procedures,
                        tvInstructions: response.property.tv_instructions,
                        fireplaceInstructions: response.property.fireplace_instructions,
                        supportContact: response.property.support_contact,
                        localRecommendations: response.property.local_recommendations,
                        additionalInstructions: response.property.additional_instructions ? (typeof response.property.additional_instructions === 'string' ? JSON.parse(response.property.additional_instructions) : response.property.additional_instructions) : []
                    };
                } else if (transformedPage.propertyId) {
                    // Fallback: try to get from context if property data not in response
                    transformedProperty = getPropertyById(transformedPage.propertyId);
                }
                
                if (!transformedProperty) {
                    setError('Property information not found');
                    setLoading(false);
                    return;
                }
                
                setGuestPage(transformedPage);
                setProperty(transformedProperty);
                setLoading(false);
            } catch (err) {
                console.error('Error loading guest page:', err);
                setError(err.message || 'Failed to load guest page');
                setLoading(false);
            }
        };
        
        loadData();
    }, [slug, getGuestPageBySlug, getPropertyById, isInitialized, user]);

    useEffect(() => {
        if (user && property && user.id === property.ownerId) {
            setIsAuthenticated(true);
        }
    }, [user, property]);

    const handleAuthentication = (success) => {
        setIsAuthenticated(success);
    };

    const handleLogout = () => {
        setIsAuthenticated(false);
        toast.success("You have been logged out.");
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><span className="text-red-600 text-2xl">⚠️</span></div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
                    <p className="text-gray-600">{error}</p>
                </motion.div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <AccessForm guestPage={guestPage} property={property} onAuthenticate={handleAuthentication} />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PortalContent guestPage={guestPage} property={property} onLogout={handleLogout} />
            <AIAssistant property={property} guestPage={guestPage} />
        </div>
    );
};

export default GuestPortal;